import posthog from 'posthog-js';

// Cookie consent cookie name (must match react-cookie-consent)
const CONSENT_COOKIE_NAME = 'smartblocek_cookie_consent';
const ANALYTICS_CONSENT_COOKIE_NAME = 'smartblocek_analytics_consent';

/**
 * Check if user has given analytics consent
 */
export const hasAnalyticsConsent = (): boolean => {
  const consent = localStorage.getItem(ANALYTICS_CONSENT_COOKIE_NAME);
  return consent === 'true';
};

/**
 * Check if user has declined analytics
 */
export const hasDeclinedAnalytics = (): boolean => {
  const consent = localStorage.getItem(ANALYTICS_CONSENT_COOKIE_NAME);
  return consent === 'false';
};

/**
 * Initialize PostHog analytics with consent-based tracking
 * @param identified - If true, track with user identification. If false, track anonymously.
 */
export const initPostHog = (identified: boolean = false) => {
  // Support both REACT_APP_ and VITE_ prefixes for environment variables
  const apiKey = import.meta.env.REACT_APP_POSTHOG_API_KEY ||
                 import.meta.env.VITE_POSTHOG_API_KEY;
  const host = import.meta.env.REACT_APP_POSTHOG_HOST ||
               import.meta.env.VITE_POSTHOG_HOST;

  if (!apiKey || !host) {
    console.warn('PostHog API key or host not configured. Analytics disabled.');
    return;
  }

  // Don't re-initialize if already loaded
  if (posthog.__loaded) {
    console.log('PostHog already initialized');
    return;
  }

  posthog.init(apiKey, {
    api_host: host,
    ui_host: 'https://eu.posthog.com', // EU Cloud

    // GDPR-friendly settings
    persistence: 'localStorage', // Use localStorage instead of cookies
    autocapture: false, // Disable automatic event capture for privacy control
    capture_pageview: false, // Manual pageview tracking for better control

    // Disable session recording by default (can enable later if needed)
    disable_session_recording: true,

    // Development vs Production
    loaded: (posthog) => {
      if (import.meta.env.MODE === 'development') {
        console.log('PostHog loaded (dev mode - tracking disabled)');
        posthog.opt_out_capturing(); // Don't track in development
      } else {
        if (identified) {
          console.log('PostHog loaded (identified tracking - user consented)');
        } else {
          console.log('PostHog loaded (anonymous tracking - user declined)');
        }
      }
    }
  });

  // Store consent preference
  localStorage.setItem(ANALYTICS_CONSENT_COOKIE_NAME, identified ? 'true' : 'false');
};

/**
 * Identify a user in PostHog
 * Call this after successful login (ONLY if user gave analytics consent)
 */
export const identifyUser = (userId: string, properties?: Record<string, any>) => {
  if (posthog.__loaded && hasAnalyticsConsent()) {
    posthog.identify(userId, properties);
    console.log('PostHog user identified:', userId);
  }
};

/**
 * Track a custom event
 */
export const trackEvent = (eventName: string, properties?: Record<string, any>) => {
  if (posthog.__loaded) {
    posthog.capture(eventName, properties);
  }
};

/**
 * Reset PostHog user (on logout)
 */
export const resetPostHog = () => {
  if (posthog.__loaded) {
    posthog.reset();
  }
};

export default posthog;
